// src/utils/notificationSound.ts

import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NOTIFICATION_PREFERENCES_STORAGE_KEY,
} from "../constants/Settings.constants";

import type {
  GlobalNotificationPreferences,
  HospitalNotificationPreferences,
  InsuranceNotificationPreferences,
  LaboratoryNotificationPreferences,
  NotificationPreferences,
} from "../types/Settings.types";

export type NotificationPortal =
  | "global"
  | "hospital"
  | "laboratory"
  | "insurance";

export type NotificationPreferencePath =
  | {
      portal: "global";
      key: keyof GlobalNotificationPreferences;
    }
  | {
      portal: "hospital";
      key: keyof HospitalNotificationPreferences;
    }
  | {
      portal: "laboratory";
      key: keyof LaboratoryNotificationPreferences;
    }
  | {
      portal: "insurance";
      key: keyof InsuranceNotificationPreferences;
    };

/* -------------------------------------------------------------------------- */
/*                            Preference utilities                            */
/* -------------------------------------------------------------------------- */

const isRecord = (
  value: unknown
): value is Record<string, unknown> => {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
};

export const mergeNotificationPreferences = (
  stored?: Partial<NotificationPreferences> | null
): NotificationPreferences => {
  return {
    global: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.global,
      ...(stored?.global ?? {}),
    },

    hospital: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.hospital,
      ...(stored?.hospital ?? {}),
    },

    laboratory: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.laboratory,
      ...(stored?.laboratory ?? {}),
    },

    insurance: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.insurance,
      ...(stored?.insurance ?? {}),
    },
  };
};

/**
 * Migrates the previous flat preference object into the new
 * grouped preference structure.
 */
const migrateLegacyPreferences = (
  value: Record<string, unknown>
): NotificationPreferences => {
  const preferences: NotificationPreferences = {
    global: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.global,
    },

    hospital: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.hospital,
    },

    laboratory: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.laboratory,
    },

    insurance: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.insurance,
    },
  };

  const assignBoolean = (
    target: Record<string, boolean>,
    key: string
  ) => {
    if (typeof value[key] === "boolean") {
      target[key] = value[key] as boolean;
    }
  };

  assignBoolean(
    preferences.global as unknown as Record<string, boolean>,
    "emailNotifications"
  );

  assignBoolean(
    preferences.global as unknown as Record<string, boolean>,
    "smsNotifications"
  );

  assignBoolean(
    preferences.global as unknown as Record<string, boolean>,
    "systemUpdates"
  );

  [
    "newAppointments",
    "appointmentChanges",
    "consultationReminders",
    "newPatientAssignments",
    "urgentPatientAlerts",
    "testResultUpdates",
    "staffAccountUpdates",
    "facilityProfileUpdates",
  ].forEach((key) => {
    assignBoolean(
      preferences.hospital as unknown as Record<string, boolean>,
      key
    );
  });

  [
    "newTestRequests",
    "urgentTests",
    "completedTests",
    "sampleCollected",
    "qualityAlerts",
    "equipmentAlerts",
    "lowInventory",
  ].forEach((key) => {
    assignBoolean(
      preferences.laboratory as unknown as Record<
        string,
        boolean
      >,
      key
    );
  });

  [
    "newClaims",
    "urgentPreAuths",
    "completedVerifications",
    "claimUpdates",
    "enrollmentUpdates",
  ].forEach((key) => {
    assignBoolean(
      preferences.insurance as unknown as Record<
        string,
        boolean
      >,
      key
    );
  });

  return preferences;
};

export const loadNotificationPreferences =
  (): NotificationPreferences => {
    if (typeof window === "undefined") {
      return {
        global: {
          ...DEFAULT_NOTIFICATION_PREFERENCES.global,
        },
        hospital: {
          ...DEFAULT_NOTIFICATION_PREFERENCES.hospital,
        },
        laboratory: {
          ...DEFAULT_NOTIFICATION_PREFERENCES.laboratory,
        },
        insurance: {
          ...DEFAULT_NOTIFICATION_PREFERENCES.insurance,
        },
      };
    }

    try {
      const storedValue = window.localStorage.getItem(
        NOTIFICATION_PREFERENCES_STORAGE_KEY
      );

      if (!storedValue) {
        return mergeNotificationPreferences();
      }

      const parsed: unknown = JSON.parse(storedValue);

      if (!isRecord(parsed)) {
        return mergeNotificationPreferences();
      }

      const isGroupedStructure =
        isRecord(parsed.global) ||
        isRecord(parsed.hospital) ||
        isRecord(parsed.laboratory) ||
        isRecord(parsed.insurance);

      if (isGroupedStructure) {
        return mergeNotificationPreferences(
          parsed as Partial<NotificationPreferences>
        );
      }

      return migrateLegacyPreferences(parsed);
    } catch (error) {
      console.error(
        "Failed to load notification preferences:",
        error
      );

      return mergeNotificationPreferences();
    }
  };

export const saveNotificationPreferences = (
  preferences: NotificationPreferences
): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    window.localStorage.setItem(
      NOTIFICATION_PREFERENCES_STORAGE_KEY,
      JSON.stringify(preferences)
    );

    return true;
  } catch (error) {
    console.error(
      "Failed to save notification preferences:",
      error
    );

    return false;
  }
};

export const isNotificationPreferenceEnabled = (
  path: NotificationPreferencePath,
  preferences: NotificationPreferences
): boolean => {
  switch (path.portal) {
    case "global":
      return preferences.global[path.key];

    case "hospital":
      return preferences.hospital[path.key];

    case "laboratory":
      return preferences.laboratory[path.key];

    case "insurance":
      return preferences.insurance[path.key];

    default:
      return true;
  }
};

export const shouldPlaySound = (
  path: NotificationPreferencePath,
  soundEnabled: boolean,
  preferences: NotificationPreferences
): boolean => {
  if (!soundEnabled) {
    return false;
  }

  return isNotificationPreferenceEnabled(
    path,
    preferences
  );
};

/* -------------------------------------------------------------------------- */
/*                                Sound player                                */
/* -------------------------------------------------------------------------- */

export const playNotificationSound = (
  volume = 0.2
): void => {
  if (
    typeof window === "undefined" ||
    typeof Audio === "undefined"
  ) {
    return;
  }

  try {
    const audio = new Audio(
      "/sounds/notification.mp3"
    );

    audio.volume = Math.min(
      1,
      Math.max(0, volume)
    );

    void audio.play().catch(() => {
      playFallbackNotificationSound(volume);
    });
  } catch {
    playFallbackNotificationSound(volume);
  }
};

const playFallbackNotificationSound = (
  volume = 0.2
): void => {
  try {
    const AudioContextClass =
      window.AudioContext ||
      (
        window as typeof window & {
          webkitAudioContext?: typeof AudioContext;
        }
      ).webkitAudioContext;

    if (!AudioContextClass) {
      return;
    }

    const audioContext = new AudioContextClass();
    const oscillator =
      audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = "sine";

    const safeVolume = Math.min(
      0.4,
      Math.max(0.01, volume)
    );

    gainNode.gain.setValueAtTime(
      safeVolume,
      audioContext.currentTime
    );

    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.35
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(
      audioContext.currentTime + 0.35
    );

    oscillator.addEventListener("ended", () => {
      void audioContext.close();
    });
  } catch {
    // Notification sounds are optional.
  }
};